import {PageHeader} from '@primer/react/experimental'
import {useStickyHeader} from '@github-ui/use-sticky-header/useStickyHeader'
import {PullRequestStateLabel} from './PullRequestStateLabel'
import type {PullRequestState} from '../types'
import {PullRequestHeaderSummary} from './PullRequestHeaderSummary'
import type {Repository} from '@github-ui/current-repository'
import stickyStyles from '@github-ui/use-sticky-header/use-sticky-header.module.css'
import {clsx} from 'clsx'
import {ObservableBox as StickyHeaderActivationThreshold} from '@github-ui/use-sticky-header/ObservableBox'
import styles from './StickyPullRequestHeader.module.css'

export const condensedResponsiveWrapperClasses = 'container-xl px-3 px-md-4 px-lg-5'
export const fullWidthResponsiveWrapperClasses = 'px-3 px-md-4 px-lg-5'

export interface StickyPullRequestHeaderData {
  author: {
    login: string
    displayName?: string | null
  }
  baseBranch: string
  commitsCount: number
  headBranch: string
  headRepositoryOwnerLogin?: string
  headRepositoryName?: string
  id: number
  isInAdvisoryRepo: boolean
  mergedBy?: string | null
  mergedByName?: string | null
  number: number
  state: PullRequestState
  title: string
  url: string
}

export interface StickyPullRequestHeaderProps {
  repository: Repository
  pullRequest: StickyPullRequestHeaderData
  pageVariant: 'condensed' | 'full-width'
}

export function StickyPullRequestHeader({repository, pullRequest, pageVariant}: StickyPullRequestHeaderProps) {
  const {isSticky, observe, unobserve} = useStickyHeader()
  const responsiveWrapperClasses =
    pageVariant === 'condensed' ? condensedResponsiveWrapperClasses : fullWidthResponsiveWrapperClasses

  return (
    <>
      <PageHeader
        className={clsx(
          stickyStyles.stickyHeader,
          styles.prHeader,
          isSticky && styles['is-stuck'],
          'color-shadow-small',
        )}
      >
        <PageHeader.TitleArea
          className={clsx('flex-items-center justify-center', responsiveWrapperClasses, styles.prTitleArea)}
        >
          <PageHeader.LeadingVisual>
            <PullRequestStateLabel state={pullRequest.state} />
          </PageHeader.LeadingVisual>
          <PageHeader.Title className="lh-condensed">
            <div>
              <a href="#top" className="fgColor-default">
                <bdi className="f5 text-bold">{pullRequest.title}</bdi>
              </a>
              <span className="f5 text-normal pl-2 fgColor-muted d-inline">#{pullRequest.number}</span>
              <div className="f6 text-normal">
                <PullRequestHeaderSummary
                  author={pullRequest.author}
                  baseBranch={pullRequest.baseBranch}
                  baseRepositoryOwnerLogin={repository.ownerLogin}
                  baseRepositoryName={repository.name}
                  commitsCount={pullRequest.commitsCount}
                  headBranch={pullRequest.headBranch}
                  headRepositoryOwnerLogin={pullRequest.headRepositoryOwnerLogin}
                  headRepositoryName={pullRequest.headRepositoryName}
                  isInAdvisoryRepo={pullRequest.isInAdvisoryRepo}
                  mergedBy={pullRequest.mergedBy}
                  mergedByName={pullRequest.mergedByName}
                  pullRequestId={pullRequest.id}
                  pullRequestNumber={pullRequest.number}
                  repositoryId={repository.id}
                  state={pullRequest.state}
                />
              </div>
            </div>
          </PageHeader.Title>
        </PageHeader.TitleArea>
      </PageHeader>
      <StickyHeaderActivationThreshold
        sx={{visibility: 'hidden', height: '1px'}}
        onObserve={observe}
        onUnobserve={unobserve}
      />
    </>
  )
}

try{ StickyPullRequestHeader.displayName ||= 'StickyPullRequestHeader' } catch {}